import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Search, ShoppingCart, Heart, User, Menu, X, Phone, LogOut } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { BUSINESS } from "@/lib/constants";

const nav = [
  { to: "/", label: "Home" },
  { to: "/products", label: "Shop" },
  { to: "/services", label: "Services" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const { user, logout } = useAuth();
  const { cartCount, wishlist } = useCart();
  const nav1 = useNavigate();

  const doSearch = (e) => {
    e.preventDefault();
    nav1(`/products?q=${encodeURIComponent(q)}`);
  };

  return (
    <header className="sticky top-0 z-40 bg-white/85 backdrop-blur-xl border-b border-slate-200">
      <div className="bg-blue-900 text-white text-xs">
        <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <a href={`tel:${BUSINESS.phone}`} className="flex items-center gap-1.5 hover:text-blue-200" data-testid="topbar-phone">
              <Phone className="w-3.5 h-3.5" /> {BUSINESS.phone}
            </a>
            <span className="hidden sm:inline text-blue-200">{BUSINESS.hours}</span>
          </div>
          <div className="hidden sm:flex items-center gap-4 text-blue-100">
            <Link to="/services" className="hover:text-white" data-testid="topbar-book-service">Book a Service</Link>
            <Link to="/contact" className="hover:text-white" data-testid="topbar-quote">Request a Quote</Link>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center gap-6">
        <Link to="/" className="flex items-center gap-2" data-testid="navbar-logo">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-900 to-blue-600 grid place-items-center text-white font-heading font-bold">S</div>
          <div>
            <div className="font-heading font-bold text-lg leading-none">SSB Copier</div>
            <div className="text-[10px] tracking-widest text-slate-500 uppercase">Tech & Services</div>
          </div>
        </Link>

        <form onSubmit={doSearch} className="hidden md:flex flex-1 max-w-2xl relative">
          <input
            data-testid="navbar-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search CCTV, laptops, printers, brands..."
            className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-11 pr-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 focus:bg-white"
          />
          <Search className="w-4 h-4 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2" />
        </form>

        <nav className="hidden lg:flex items-center gap-6 text-sm font-medium">
          {nav.map((n) => (
            <Link key={n.to} to={n.to} className="text-slate-700 hover:text-blue-900 transition" data-testid={`nav-${n.label.toLowerCase()}`}>
              {n.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <Link to="/wishlist" className="relative p-2 hover:bg-slate-100 rounded-lg" data-testid="navbar-wishlist">
            <Heart className="w-5 h-5 text-slate-700" />
            {wishlist.length > 0 && <span className="absolute -top-1 -right-1 bg-blue-600 text-white text-[10px] rounded-full w-4 h-4 grid place-items-center">{wishlist.length}</span>}
          </Link>
          <Link to="/cart" className="relative p-2 hover:bg-slate-100 rounded-lg" data-testid="navbar-cart">
            <ShoppingCart className="w-5 h-5 text-slate-700" />
            {cartCount > 0 && <span className="absolute -top-1 -right-1 bg-blue-900 text-white text-[10px] rounded-full w-4 h-4 grid place-items-center">{cartCount}</span>}
          </Link>
          {user ? (
            <div className="hidden sm:flex items-center gap-2">
              <Link to="/account" className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-slate-100" data-testid="navbar-account">
                <User className="w-4 h-4" /> <span className="text-sm">{user.name?.split(" ")[0]}</span>
              </Link>
              <button onClick={logout} className="p-2 hover:bg-slate-100 rounded-lg" data-testid="navbar-logout" aria-label="Logout">
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <Link to="/login" className="hidden sm:inline-flex bg-blue-900 hover:bg-blue-800 text-white text-sm px-4 py-2 rounded-xl font-medium" data-testid="navbar-login">
              Sign in
            </Link>
          )}
          <button className="lg:hidden p-2" onClick={() => setOpen(!open)} data-testid="navbar-menu-toggle" aria-label="Menu">
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-slate-200 bg-white">
          <div className="px-4 py-4 space-y-2">
            <form onSubmit={doSearch} className="relative mb-3">
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search..." className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-10 pr-4 py-2.5 text-sm" data-testid="mobile-search-input" />
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            </form>
            {nav.map((n) => (
              <Link key={n.to} to={n.to} onClick={() => setOpen(false)} className="block py-2 text-slate-700 font-medium">{n.label}</Link>
            ))}
            {!user && <Link to="/login" onClick={() => setOpen(false)} className="block py-2 text-blue-900 font-semibold">Sign in</Link>}
            {user && <Link to="/account" onClick={() => setOpen(false)} className="block py-2 text-blue-900 font-semibold">My Account</Link>}
          </div>
        </div>
      )}
    </header>
  );
}
